import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { QuillEditorComponent } from 'ngx-quill';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export type AutoSaveState = 'idle' | 'saving' | 'saved' | 'error';

/** How long the "Saved" confirmation stays up before falling back to idle. */
const SAVED_LINGER_MS = 2000;

/**
 * Debounced auto-save for a single `[(ngModel)]` field:
 * `<input [(ngModel)]="settings.webhook_url" [appAutoSave]="saveSettings$">`.
 *
 * The bound function returns the PATCH observable (no success toast of its
 * own — the status indicator is the feedback). Only user edits save: a
 * programmatic model write (initial bind, a reset, the server echo) just moves
 * the "last saved" baseline. The pending edit is flushed on blur and on
 * destroy so leaving the field or the tab never drops a change.
 */
@Directive({
  selector: '[appAutoSave]',
  exportAs: 'appAutoSave',
  standalone: false
})
export class AutoSaveDirective implements OnInit, OnDestroy {
  @Input('appAutoSave') public save: (context?: any) => Observable<unknown>;

  /** The entity being edited, for fields rendered inside a `@for`. */
  @Input() public autoSaveContext: any;
  @Input() public autoSaveDebounce: number = 800;
  @Input() public autoSaveDisabled: boolean = false;

  @Output() public autoSaveState = new EventEmitter<AutoSaveState>();

  public state: AutoSaveState = 'idle';

  private readonly _changes = new Subject<void>();
  private readonly _destroyed = new Subject<void>();

  private readonly constructorName: string = String(this.constructor.name);

  private lastSaved: any;
  private sawModelUpdate: boolean = false;
  private isSaving: boolean = false;
  private retryAfterFlight: boolean = false;
  private savedTimer: any = null;
  private isDestroyed: boolean = false;

  constructor(
    @Self() private readonly _model: NgModel,
    @Optional() @Self() private readonly _quill: QuillEditorComponent,
    private readonly _element: ElementRef<HTMLElement>,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService,
    private readonly _translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.lastSaved = this._model.value;

    // NgModel emits `update` only for view-to-model changes, and it does so
    // synchronously right after the control's `valueChanges`. So a
    // `valueChanges` with no `update` behind it in the same microtask is a
    // programmatic write, which moves the baseline instead of saving — unless
    // one of our own saves is in flight, in which case the baseline already
    // holds the value we sent and a write we did not cause (a whole-entity
    // echo from a sibling field, say) must not drag it onto a stale value.
    this._model.update
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => (this.sawModelUpdate = true));

    this._model.control.valueChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe((value: any) => {
        this.sawModelUpdate = false;

        Promise.resolve().then(() => {
          if (this.sawModelUpdate) {
            this.sawModelUpdate = false;
            this._changes.next();
          } else if (!this.isSaving) {
            this.lastSaved = value;
          }
        });
      });

    this._changes
      .pipe(debounceTime(this.autoSaveDebounce), takeUntil(this._destroyed))
      .subscribe(() => this.attempt());

    this._quill?.onBlur
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => this.flush());
  }

  ngOnDestroy(): void {
    // Fire-and-forget: the request outlives the directive.
    this.flush();

    this.isDestroyed = true;
    this._destroyed.next();
    this._destroyed.complete();
    clearTimeout(this.savedTimer);
  }

  // 'blur' covers leaving the field, 'beforeunload' the tab being closed
  // inside the debounce window.
  @HostListener('blur')
  @HostListener('window:beforeunload')
  public flush(): void {
    this.attempt();
  }

  private attempt(): void {
    if (!this.save || this.autoSaveDisabled || this._model.invalid) {
      return;
    }

    const value: any = this._model.value;
    if (this.isUnchanged(value)) {
      return;
    }

    if (this.isSaving) {
      this.retryAfterFlight = true;
      return;
    }

    this.retryAfterFlight = false;
    this.setState('saving');

    // Both of these are set before subscribing: a synchronous observable calls
    // back before `subscribe()` returns, and the baseline has to be the value
    // in flight for the whole request, not only once it lands.
    const previous: any = this.lastSaved;
    this.lastSaved = value;
    this.isSaving = true;

    this.save(this.autoSaveContext).subscribe({
      error: (err: any) => {
        this.isSaving = false;
        this.retryAfterFlight = false;
        // Back to the last value the API accepted, so the field reads as dirty
        // and the next edit or blur retries it.
        this.lastSaved = previous;

        this._logger.error(this.constructorName, this.request, err);

        this.setState('error');
        this._toast.error(
          err?.[0]?.detail || this._translate.instant('SETTINGS.AUTOSAVE.ERROR')
        );
      },
      complete: () => {
        this.isSaving = false;

        this.setState('saved');
        clearTimeout(this.savedTimer);
        if (!this.isDestroyed) {
          this.savedTimer = setTimeout(
            () => this.setState('idle'),
            SAVED_LINGER_MS
          );
        }

        if (this.retryAfterFlight) {
          this.attempt();
        }
      }
    });
  }

  /** Identifies the field in the logs: `auto-save webhook_url`, `auto-save content#42`. */
  private get request(): string {
    const field: string =
      this._model.name || this._element.nativeElement.getAttribute('id');
    const contextId: string = this.autoSaveContext?.id;

    return ['auto-save', [field, contextId].filter(Boolean).join('#')]
      .filter(Boolean)
      .join(' ');
  }

  private isUnchanged(value: any): boolean {
    return this.normalize(value) === this.normalize(this.lastSaved);
  }

  private normalize(value: any): any {
    return typeof value === 'string' ? value.trim() : value;
  }

  private setState(state: AutoSaveState): void {
    if (this.state === state) {
      return;
    }

    this.state = state;
    this.autoSaveState.emit(state);
  }
}
